import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, DoorClosed, ShieldAlert } from "lucide-react";
import { buildingsApi } from "@/services/api";
import { apiError } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function DoorList({ title, tone, doors }) {
  if (!doors.length) return null;
  const toneCls = tone === "red"
    ? "border-red-200 bg-red-50 text-red-700"
    : "border-amber-200 bg-amber-50 text-amber-800";
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${toneCls}`} data-testid={`complete-list-${tone}`}>
      <p className="text-xs font-bold uppercase tracking-wide">{title} ({doors.length})</p>
      <p className="mt-1.5 text-sm leading-relaxed">
        {doors.map((d) => d.door_reference || d.location || "door").join(", ")}
      </p>
    </div>
  );
}

export default function BuildingCompleteDialog({ buildingId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setError(""); setSummary(null);
    buildingsApi.completionSummary(buildingId)
      .then((res) => { if (alive) setSummary(res.data); })
      .catch((e) => { if (alive) setError(apiError(e, "Could not load the completion summary.")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, buildingId]);

  const neverDoors = (summary?.doors || []).filter((d) => !d.ever_inspected);
  const staleDoors = (summary?.doors || []).filter((d) => d.ever_inspected && !d.inspected_recently);
  const allClear = summary && summary.never_inspected_count === 0 && summary.not_recently_inspected_count === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="building-complete-dialog">
        <DialogHeader>
          <DialogTitle>Mark building complete</DialogTitle>
          <DialogDescription>Check every door has been inspected and saved before you finish.</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        )}
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {summary && !loading && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
                <p className="text-xl font-bold text-slate-900" data-testid="complete-total">{summary.total_doors}</p>
                <p className="text-xs text-slate-500">Doors saved</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
                <p className="text-xl font-bold text-slate-900">{summary.apartment_count}</p>
                <p className="text-xs text-slate-500">Apartment</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
                <p className="text-xl font-bold text-slate-900">{summary.communal_count}</p>
                <p className="text-xs text-slate-500">Communal</p>
              </div>
            </div>

            {allClear ? (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm font-medium text-emerald-700" data-testid="complete-all-clear">
                <CheckCircle2 className="h-5 w-5" /> All doors have been inspected on this visit — nothing outstanding.
              </div>
            ) : (
              <>
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Some doors still look outstanding. Please review before marking this building complete.</span>
                </div>
                <DoorList title="Never inspected" tone="red" doors={neverDoors} />
                <DoorList title="Not inspected on this visit" tone="amber" doors={staleDoors} />
              </>
            )}

            <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <span>
                A door is only recorded once it has saved to FD Check. This summary counts doors saved to the server — if you added doors on a mobile device with no signal, make sure they have finished syncing before you leave site.
              </span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="building-complete-close">
            <DoorClosed className="mr-2 h-4 w-4" /> Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
